/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { ParentSize } from "@visx/responsive";
import { useTooltipInPortal, useTooltip } from "@visx/tooltip";
import { When } from "react-if";
import { BrickLoader, UniversalWrap } from "~/components";
import {
  CostTableData,
  getEndDate,
  mapAccumulatedCostChartColors,
  StackBarChart,
} from "~/components/Charts/CostAnalysis";
import {
  getCostAnalysisTableColumns,
  tooltipStyles,
} from "~/components/Charts/CostAnalysis/components";
import {
  ChartType,
  CostAnalysisFields,
  Granularity,
  Period,
} from "~/components/Charts/CostAnalysis/types";
import { useCurrency } from "~/hooks";
import { useOrgUnitAccumulatedCostChart } from "../hooks";

type Props = {
  id: string;
  startDate: string;
  endDate: string;
  granularity: Granularity;
  period: Period;
  hasFooter: boolean;
  showCostTable: boolean;
};

export function OrgUnitAccumulatedCostView({
  id,
  startDate,
  endDate,
  granularity,
  period,
  hasFooter,
  showCostTable,
}: Props) {
  const { currency } = useCurrency();
  const {
    orgUnitAccumulatedCostChartData,
    orgUnitAccumulatedCostTableData,
    orgUnitAccumulatedCostTotal,
    loading,
    error,
  } = useOrgUnitAccumulatedCostChart({
    orgUnitId: id,
    startDate,
    endDate: getEndDate(endDate),
    period,
    granularity,
  });

  const colors = mapAccumulatedCostChartColors([CostAnalysisFields.DAILY_COST]);

  const columns = useMemo(
    () =>
      getCostAnalysisTableColumns({
        startDate,
        endDate,
        currency,
        data: orgUnitAccumulatedCostTableData,
        period,
        colors,
        chartType: ChartType.ACCUMULATED_COST,
      }),
    [
      startDate,
      endDate,
      currency,
      orgUnitAccumulatedCostTableData,
      period,
      colors,
    ]
  );

  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    detectBounds: true,
    scroll: true,
  });

  const {
    showTooltip,
    hideTooltip,
    tooltipOpen,
    tooltipData,
    tooltipLeft,
    tooltipTop,
  } = useTooltip();

  return (
    <UniversalWrap
      loaderProps={{ loading, Component: BrickLoader }}
      errorProps={{ error }}
    >
      <ParentSize>
        {(parent) => (
          <>
            <StackBarChart
              width={parent.width}
              height={280}
              data={orgUnitAccumulatedCostChartData}
              granularity={granularity}
              loading={loading}
              startDate={startDate}
              endDate={endDate}
              period={period}
              showTooltip={showTooltip}
              hideTooltip={hideTooltip}
              keys={[CostAnalysisFields.DAILY_COST]}
              colors={colors}
              chartType={ChartType.ACCUMULATED_COST}
              containerRef={containerRef}
            />

            <When condition={tooltipOpen}>
              <TooltipInPortal
                // set this to random so it correctly updates with parent bounds
                key={Date.now()}
                top={tooltipTop}
                left={tooltipLeft}
                style={tooltipStyles}
              >
                {tooltipData}
              </TooltipInPortal>
            </When>
          </>
        )}
      </ParentSize>
      <CostTableData
        period={period}
        startDate={startDate}
        endDate={endDate}
        tableData={orgUnitAccumulatedCostTableData}
        chartType={ChartType.ACCUMULATED_COST}
        total={orgUnitAccumulatedCostTotal}
        tableError={error}
        columns={columns}
        isLoading={loading}
        showCostTable={showCostTable}
        hasFooter={hasFooter}
      />
    </UniversalWrap>
  );
}
